import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const authHeader = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

export const fetchRiskPermissions = createAsyncThunk(
  "riskAnalysis/fetchPermissions",
  async (organizationId, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/user-permissions/${organizationId}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchMissionGuestData = createAsyncThunk(
  "riskAnalysis/fetchMissionGuestData",
  async ({ organizationId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      let path = "";

      if (Number(organizationId) === 2) {
        path = `/get_dnk_actions_data?start_date=${startDate}&end_date=${endDate}`;
      } else if (Number(organizationId) === 19) {
        path = `/gentofte-life-actions-data?start_date=${startDate}&end_date=${endDate}`;
      } else {
        throw new Error("Mission/guest data is only available for org 2 or 19.");
      }

      const url = `${baseUrl}${path}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchRiskRecords = createAsyncThunk(
  "riskAnalysis/fetchRiskRecords",
  async (organizationId, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/risk-analysis/${organizationId}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchRiskActionCards = createAsyncThunk(
  "riskAnalysis/fetchRiskActionCards",
  async (organizationId, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/action-cards-with-prototype/${organizationId}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchOrganisationEmployees = createAsyncThunk(
  "riskAnalysis/fetchOrganisationEmployees",
  async (organizationId, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/organisation-employee/${organizationId}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchEstimatedRescueData = createAsyncThunk(
  "riskAnalysis/fetchEstimatedRescueData",
  async (organizationId, { rejectWithValue }) => {
    try {
      if (Number(organizationId) !== 19) {
        throw new Error("Estimated rescue data is only available for org 19.");
      }

      const url = `${process.env.REACT_APP_API_URL}/gentoftelife-estimated-rescue`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchFilterOptions = createAsyncThunk(
  "riskAnalysis/fetchFilterOptions",
  async (organizationId, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/filter-keys/${organizationId}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchHrManagementData = createAsyncThunk(
  "riskAnalysis/fetchHrManagementData",
  async (params, { rejectWithValue }) => {
    try {
      const {
        organizationId,
        dateFrom = "All",
        dateTo = "All",
        instructor = "All",
        location = "All",
        klr = "All",
        lifeguard = "All",
        timeRange,
      } = params;

      const orgId = Number(organizationId);
      const searchParams = new URLSearchParams();
      const appendIfNeeded = (key, value) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            searchParams.append(key, `[${value.join(",")}]`);
          }
          return;
        }

        if (value !== undefined && value !== null && String(value).trim() !== "") {
          searchParams.append(key, value);
        }
      };

      const hasSelectedFilter = [dateFrom, dateTo, instructor, location, klr, lifeguard].some(
        (value) => value !== undefined && value !== null && String(value).trim() !== "" && value !== "All"
      );

      if (hasSelectedFilter) {
        if (dateFrom !== "All") appendIfNeeded("dateFrom", dateFrom);
        if (dateTo !== "All") appendIfNeeded("dateTo", dateTo);
        if (instructor !== "All") appendIfNeeded("instructor", instructor);
        if (location !== "All") appendIfNeeded("location", location);
        if (orgId === 2 && klr !== "All") appendIfNeeded("klr", klr);
        if (orgId === 19 && lifeguard !== "All") appendIfNeeded("lifeguard", lifeguard);
      }

      appendIfNeeded("timeRange", timeRange);

      const query = searchParams.toString();
      const url = `${process.env.REACT_APP_API_URL}/hr-management/${organizationId}${
        query ? `?${query}` : ""
      }`;
      const { data } = await axios.get(url, { headers: authHeader() });
      return data?.data || data || [];
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data || error.message;
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  permissions: {
    data: null,
    loading: false,
    error: null,
    success: false,
  },
  riskRecords: {
    data: [],
    loading: false,
    error: null,
    success: false,
  },
  actionCards: {
    data: [],
    loading: false,
    error: null,
    success: false,
  },
  employees: {
    data: [],
    loading: false,
    error: null,
    success: false,
  },
  missionGuest: {
    data: [],
    loading: false,
    error: null,
    success: false,
    params: null,
  },
  estimatedRescue: {
    data: [],
    loading: false,
    error: null,
    success: false,
  },
  hrFilters: {
    data: [],
    loading: false,
    error: null,
    success: false,
  },
  hrManagement: {
    data: [],
    loading: false,
    error: null,
    success: false,
    params: null,
  },
};

const riskAnalysisSlice = createSlice({
  name: "riskAnalysis",
  initialState,
  reducers: {
    resetMissionGuestState: (state) => {
      state.missionGuest = { ...initialState.missionGuest };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRiskPermissions.pending, (state) => {
        state.permissions.loading = true;
        state.permissions.error = null;
        state.permissions.success = false;
      })
      .addCase(fetchRiskPermissions.fulfilled, (state, action) => {
        state.permissions.loading = false;
        state.permissions.data = action.payload;
        state.permissions.success = true;
      })
      .addCase(fetchRiskPermissions.rejected, (state, action) => {
        state.permissions.loading = false;
        state.permissions.error = action.payload;
        state.permissions.success = false;
      })

      .addCase(fetchRiskRecords.pending, (state) => {
        state.riskRecords.loading = true;
        state.riskRecords.error = null;
        state.riskRecords.success = false;
      })
      .addCase(fetchRiskRecords.fulfilled, (state, action) => {
        state.riskRecords.loading = false;
        state.riskRecords.data = action.payload ?? [];
        state.riskRecords.success = true;
      })
      .addCase(fetchRiskRecords.rejected, (state, action) => {
        state.riskRecords.loading = false;
        state.riskRecords.error = action.payload;
        state.riskRecords.success = false;
      })

      .addCase(fetchRiskActionCards.pending, (state) => {
        state.actionCards.loading = true;
        state.actionCards.error = null;
        state.actionCards.success = false;
      })
      .addCase(fetchRiskActionCards.fulfilled, (state, action) => {
        state.actionCards.loading = false;
        state.actionCards.data = action.payload ?? [];
        state.actionCards.success = true;
      })
      .addCase(fetchRiskActionCards.rejected, (state, action) => {
        state.actionCards.loading = false;
        state.actionCards.error = action.payload;
        state.actionCards.success = false;
      })

      .addCase(fetchOrganisationEmployees.pending, (state) => {
        state.employees.loading = true;
        state.employees.error = null;
        state.employees.success = false;
      })
      .addCase(fetchOrganisationEmployees.fulfilled, (state, action) => {
        state.employees.loading = false;
        state.employees.data = action.payload ?? [];
        state.employees.success = true;
      })
      .addCase(fetchOrganisationEmployees.rejected, (state, action) => {
        state.employees.loading = false;
        state.employees.error = action.payload;
        state.employees.success = false;
      })

      .addCase(fetchMissionGuestData.pending, (state, action) => {
        state.missionGuest.loading = true;
        state.missionGuest.error = null;
        state.missionGuest.success = false;
        state.missionGuest.params = action.meta.arg;
      })
      .addCase(fetchMissionGuestData.fulfilled, (state, action) => {
        state.missionGuest.loading = false;
        state.missionGuest.data = action.payload ?? [];
        state.missionGuest.success = true;
      })
      .addCase(fetchMissionGuestData.rejected, (state, action) => {
        state.missionGuest.loading = false;
        state.missionGuest.error = action.payload;
        state.missionGuest.success = false;
      })

      .addCase(fetchEstimatedRescueData.pending, (state) => {
        state.estimatedRescue.loading = true;
        state.estimatedRescue.error = null;
        state.estimatedRescue.success = false;
      })
      .addCase(fetchEstimatedRescueData.fulfilled, (state, action) => {
        state.estimatedRescue.loading = false;
        state.estimatedRescue.data = action.payload ?? [];
        state.estimatedRescue.success = true;
      })
      .addCase(fetchEstimatedRescueData.rejected, (state, action) => {
        state.estimatedRescue.loading = false;
        state.estimatedRescue.error = action.payload;
        state.estimatedRescue.success = false;
      })

      .addCase(fetchFilterOptions.pending, (state) => {
        state.hrFilters.loading = true;
        state.hrFilters.error = null;
        state.hrFilters.success = false;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.hrFilters.loading = false;
        state.hrFilters.data = action.payload ?? [];
        state.hrFilters.success = true;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        state.hrFilters.loading = false;
        state.hrFilters.error = action.payload;
        state.hrFilters.success = false;
      })

      .addCase(fetchHrManagementData.pending, (state, action) => {
        state.hrManagement.loading = true;
        state.hrManagement.error = null;
        state.hrManagement.success = false;
        state.hrManagement.params = action.meta?.arg ?? null;
      })
      .addCase(fetchHrManagementData.fulfilled, (state, action) => {
        state.hrManagement.loading = false;
        state.hrManagement.data = action.payload ?? [];
        state.hrManagement.success = true;
      })
      .addCase(fetchHrManagementData.rejected, (state, action) => {
        state.hrManagement.loading = false;
        state.hrManagement.error = action.payload;
        state.hrManagement.success = false;
      });
  },
});

export const { resetMissionGuestState } = riskAnalysisSlice.actions;

export default riskAnalysisSlice.reducer;
